import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchCompanyInfo } from '../services/companyInfo';
import { io } from 'socket.io-client';
import {
  AppBar, Toolbar, Typography, Box, Button, IconButton,
  Menu, MenuItem, Avatar, Drawer, List, ListItem, ListItemIcon,
  ListItemText, useMediaQuery, useTheme, Divider, Snackbar, Alert
} from '@mui/material';
import {
  LocalShipping, Dashboard, AddBox, ListAlt, ManageAccounts,
  Menu as MenuIcon, AccountCircle, Logout, Close
} from '@mui/icons-material';

const SOCKET_URL = (process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/, '') || window.location.origin;

// 한국어 TTS — 음성 목록 로드 대기 + ko 보이스 선택. cancel()을 speak 직전에 호출하지 않음(취소 버그 회피).
function speakKo(text) {
  try {
    const synth = window.speechSynthesis;
    if (!synth || !text) return;
    const utter = () => {
      try {
        const u = new window.SpeechSynthesisUtterance(text);
        u.lang = 'ko-KR'; u.rate = 1.0; u.pitch = 1.0;
        const vs = synth.getVoices() || [];
        const ko = vs.find((v) => (v.lang || '').toLowerCase().startsWith('ko'));
        if (ko) u.voice = ko;
        try { synth.resume(); } catch (e) { /* ignore */ }
        synth.speak(u);
      } catch (e) { /* ignore */ }
    };
    if ((synth.getVoices() || []).length === 0) {
      let done = false;
      const once = () => { if (!done) { done = true; utter(); } };
      try { synth.onvoiceschanged = once; } catch (e) { /* ignore */ }
      setTimeout(once, 300);
    } else {
      utter();
    }
  } catch (e) { /* ignore */ }
}

// 최초 사용자 제스처에 TTS 잠금 해제 (브라우저 autoplay 정책상 제스처 전엔 speak가 무시됨)
let _ttsUnlocked = false;
function primeTtsOnce() {
  if (_ttsUnlocked) return;
  const prime = () => {
    _ttsUnlocked = true;
    try { window.speechSynthesis && window.speechSynthesis.speak(new window.SpeechSynthesisUtterance('')); } catch (e) { /* ignore */ }
    window.removeEventListener('pointerdown', prime);
    window.removeEventListener('keydown', prime);
    window.removeEventListener('touchstart', prime);
  };
  window.addEventListener('pointerdown', prime);
  window.addEventListener('keydown', prime);
  window.addEventListener('touchstart', prime);
}

const navItems = [
  { label: '대시보드', path: '/dashboard', icon: <Dashboard /> },
  { label: '배차 주문', path: '/orders/new', icon: <AddBox /> },
  { label: '내 주문 목록', path: '/orders', icon: <ListAlt /> },
  { label: '내 정보', path: '/account', icon: <ManageAccounts /> },
];

export default function CustomerLayout({ children, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const user = JSON.parse(localStorage.getItem('customer_user') || '{}');
  const displayName = user.company_name || user.name || '고객';
  const [company, setCompany] = useState({ company_name: '', company_phone: '' });
  const companyRef = useRef({ company_name: '' });
  const [pushSnack, setPushSnack] = useState({ open: false, title: '', body: '' });
  useEffect(() => {
    fetchCompanyInfo().then((c) => { setCompany(c); companyRef.current = c; }).catch(() => {});
  }, []);

  useEffect(() => { primeTtsOnce(); }, []);

  useEffect(() => {
    if (!localStorage.getItem('customer_token')) return;
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const showPush = (title, body) => {
      setPushSnack({ open: true, title: title || '알림', body: body || '' });
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try { new Notification(title || '알림', { body: body || '' }); } catch (e) { /* ignore */ }
      }
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      } catch (e) { /* ignore */ }
      // TTS — "써니왔어! {title}" (emoji 제거, 80자 제한)
      try {
        const ttsOn = localStorage.getItem('ttsEnabled');
        if (ttsOn === null || ttsOn === '1') {
          const cleanTitle = String(title || '').replace(/[\p{Extended_Pictographic}\p{Emoji_Component}]/gu, '').replace(/\s+/g, ' ').trim();
          const phrase = cleanTitle ? `써니왔어! ${cleanTitle}`.slice(0, 80) : '써니왔어!';
          speakKo(phrase);
        }
      } catch (e) { /* ignore */ }
    };

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] });
    socket.on('connect', () => console.log('[CustomerLayout Socket] 연결됨'));
    socket.on('order-updated', (data) => {
      if (data?.type === 'price_updated') {
        const p = Number(data?.client_offered_price) || 0;
        showPush('💰 금액 변경 안내', p ? `제시 금액이 ${p.toLocaleString()}원(으)로 변경되었습니다.` : '제시 금액이 변경되었습니다. 확인해주세요.');
      } else if (data?.type === 'price_proposed') {
        showPush('💰 배차 금액 안내', '배차 금액이 제시되었습니다. 확인해주세요.');
      } else if (data?.status === 'assigned') {
        showPush('🚚 배차 완료', '배차가 완료되었습니다.');
      } else if (data?.status === 'in_progress') {
        showPush('🚛 운송 시작', '운송이 시작되었습니다.');
      } else if (data?.status === 'completed') {
        showPush('✅ 배송 완료', '배송이 완료되었습니다.');
      }
    });
    socket.on('order-message', (data) => {
      if (data.senderType === 'staff') {
        const cn = companyRef.current.company_name;
        showPush(`💬 ${cn ? cn + ' ' : ''}메시지`, `[${data.orderNumber}] ${data.message || ''}`);
      }
    });
    return () => { socket.disconnect(); };
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="sticky" sx={{ bgcolor: '#1565c0' }}>
        <Toolbar>
          {/* 항상 햄버거 버튼 표시 (모바일/태블릿 모두) */}
          <IconButton color="inherit" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
            <MenuIcon />
          </IconButton>
          <LocalShipping sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1, cursor: 'pointer' }}
            onClick={() => navigate('/dashboard')}>
            {company.company_name || ''}
          </Typography>

          {/* 데스크톱에서만 상단 버튼도 병행 표시 */}
          {!isMobile && navItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                mx: 0.5,
                display: { xs: 'none', md: 'inline-flex' },
                fontWeight: location.pathname === item.path ? 700 : 400,
                borderBottom: location.pathname === item.path ? '2px solid white' : 'none',
                borderRadius: 0,
              }}
            >
              {item.label}
            </Button>
          ))}

          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: '#e65100', fontSize: '0.9rem' }}>
              {displayName[0]}
            </Avatar>
          </IconButton>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled>
              <AccountCircle sx={{ mr: 1 }} fontSize="small" />
              {displayName}
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }}>
              <Logout sx={{ mr: 1 }} fontSize="small" />
              로그아웃
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* 드로어 메뉴 */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 240, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* 드로어 헤더 */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, bgcolor: '#1565c0', color: 'white' }}>
            <LocalShipping sx={{ mr: 1 }} />
            <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
              {company.company_name || ''}
            </Typography>
            <IconButton color="inherit" size="small" onClick={() => setDrawerOpen(false)}>
              <Close />
            </IconButton>
          </Box>
          <Divider />

          <List sx={{ flexGrow: 1 }}>
            {navItems.map((item) => (
              <ListItem
                button
                key={item.path}
                selected={location.pathname === item.path}
                onClick={() => { navigate(item.path); setDrawerOpen(false); }}
                sx={{
                  '&.Mui-selected': { bgcolor: '#e3f2fd', borderLeft: '4px solid #1565c0' },
                  borderLeft: '4px solid transparent',
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? '#1565c0' : 'inherit' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: location.pathname === item.path ? 700 : 400 }}
                />
              </ListItem>
            ))}
          </List>

          <Divider />
          <List>
            <ListItem button onClick={onLogout}>
              <ListItemIcon><Logout /></ListItemIcon>
              <ListItemText primary="로그아웃" />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto', width: '100%' }}>
        {children}
      </Box>

      <Box component="footer" sx={{ py: 2, textAlign: 'center', bgcolor: '#f0f0f0' }}>
        <Typography variant="caption" color="text.secondary">
          © {new Date().getFullYear()} {company.company_name || ''}
          {company.company_phone ? ` · 고객센터 ${company.company_phone}` : ''}
        </Typography>
      </Box>

      <Snackbar
        open={pushSnack.open}
        autoHideDuration={6000}
        onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }}
      >
        <Alert
          onClose={() => setPushSnack((s) => ({ ...s, open: false }))}
          severity="info"
          variant="filled"
          sx={{ minWidth: 300, cursor: 'pointer' }}
          onClick={() => { navigate('/orders'); setPushSnack((s) => ({ ...s, open: false })); }}
        >
          <Typography variant="subtitle2" fontWeight={700}>{pushSnack.title}</Typography>
          <Typography variant="body2">{pushSnack.body}</Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
}
